import { useState } from "react";
import { z } from "zod";
import { Mail, Send, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const SUPPORT_EMAIL = "support@hari.co.id";

/**
 * Lightweight, fully client-side contact form. We validate input with Zod
 * (length + email checks) then open the user's mail client pre-filled with
 * subject + body addressed to support@hari.co.id. This guarantees delivery
 * via the user's own SMTP without us having to provision a mail backend,
 * and keeps the contactPoint schema honest.
 */
export function ContactForm({
  locale = "id",
  defaultTopic,
  prefillMessage = "",
}: {
  locale?: "id" | "en";
  defaultTopic?: string;
  prefillMessage?: string;
}) {
  const t = locale === "en" ? COPY.en : COPY.id;
  const initialTopic =
    (defaultTopic && t.topics.find((x) => x.toLowerCase() === defaultTopic.toLowerCase())) ||
    defaultTopic ||
    t.topics[0];
  const [state, setState] = useState({
    name: "",
    email: "",
    topic: initialTopic,
    message: prefillMessage,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  function update<K extends keyof typeof state>(k: K, v: (typeof state)[K]) {
    setState((s) => ({ ...s, [k]: v }));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const schema = z.object({
      name: z.string().trim().min(2, t.errName).max(80),
      email: z.string().trim().email(t.errEmail).max(160),
      topic: z.string().trim().min(1).max(80),
      message: z.string().trim().min(10, t.errMsg).max(2000),
    });
    const parsed = schema.safeParse(state);
    if (!parsed.success) {
      const fe: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const k = String(issue.path[0] ?? "");
        if (k && !fe[k]) fe[k] = issue.message;
      }
      setErrors(fe);
      return;
    }
    setErrors({});
    setBusy(true);
    const subject = `[${parsed.data.topic}] ${parsed.data.name}`;
    const bodyLines = [
      `${t.lblName}: ${parsed.data.name}`,
      `${t.lblEmail}: ${parsed.data.email}`,
      `${t.lblTopic}: ${parsed.data.topic}`,
      "",
      parsed.data.message,
      "",
      `— ${t.sentVia} hari.co.id`,
    ].join("\n");
    const url = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyLines)}`;
    if (typeof window !== "undefined") {
      window.location.href = url;
    }
    setTimeout(() => {
      setBusy(false);
      setSent(true);
    }, 600);
  }

  if (sent) {
    return (
      <div className="not-prose mt-2 rounded-xl border border-green-500/30 bg-green-500/5 p-5">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="mt-0.5 h-5 w-5 text-green-600" />
          <div className="space-y-1">
            <p className="font-semibold">{t.sentTitle}</p>
            <p className="text-sm text-muted-foreground">{t.sentDesc}</p>
            <a className="text-sm text-primary hover:underline" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
          </div>
        </div>
        <Button variant="ghost" size="sm" className="mt-3" onClick={() => { setSent(false); setState({ name: "", email: "", topic: t.topics[0], message: "" }); }}>
          {t.sendAnother}
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="not-prose mt-4 grid gap-4 rounded-xl border border-border bg-card p-5" noValidate>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Label htmlFor="cf-name">{t.lblName}</Label>
          <Input id="cf-name" value={state.name} onChange={(e) => update("name", e.target.value)} maxLength={80} aria-invalid={!!errors.name} />
          {errors.name && <p className="mt-1 text-xs text-destructive">{errors.name}</p>}
        </div>
        <div>
          <Label htmlFor="cf-email">{t.lblEmail}</Label>
          <Input id="cf-email" type="email" value={state.email} onChange={(e) => update("email", e.target.value)} maxLength={160} aria-invalid={!!errors.email} />
          {errors.email && <p className="mt-1 text-xs text-destructive">{errors.email}</p>}
        </div>
      </div>
      <div>
        <Label htmlFor="cf-topic">{t.lblTopic}</Label>
        <select
          id="cf-topic"
          value={state.topic}
          onChange={(e) => update("topic", e.target.value)}
          className="mt-1 block h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
        >
          {t.topics.map((tp) => <option key={tp} value={tp}>{tp}</option>)}
        </select>
      </div>
      <div>
        <Label htmlFor="cf-message">{t.lblMsg}</Label>
        <Textarea id="cf-message" rows={6} value={state.message} onChange={(e) => update("message", e.target.value)} maxLength={2000} aria-invalid={!!errors.message} />
        {errors.message && <p className="mt-1 text-xs text-destructive">{errors.message}</p>}
        <p className="mt-1 text-xs text-muted-foreground">{state.message.length}/2000</p>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
          <Mail className="h-3.5 w-3.5" /> {t.willSendTo} <a className="text-primary hover:underline" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        </p>
        <Button type="submit" disabled={busy}>
          {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t.sending}</> : <><Send className="mr-2 h-4 w-4" /> {t.send}</>}
        </Button>
      </div>
    </form>
  );
}

const COPY = {
  id: {
    lblName: "Nama",
    lblEmail: "Email",
    lblTopic: "Topik",
    lblMsg: "Pesan",
    topics: ["Koreksi data libur", "Permintaan fitur", "Laporan bug", "Kerja sama / iklan", "Lainnya"],
    send: "Kirim pesan",
    sending: "Membuka email...",
    willSendTo: "Akan dikirim ke",
    sentVia: "dikirim via",
    sentTitle: "Email Anda sudah disiapkan",
    sentDesc: "Aplikasi email Anda terbuka berisi pesan untuk tim hari.co.id. Tinggal klik kirim.",
    sendAnother: "Kirim pesan lain",
    errName: "Nama minimal 2 karakter",
    errEmail: "Email tidak valid",
    errMsg: "Pesan minimal 10 karakter",
  },
  en: {
    lblName: "Name",
    lblEmail: "Email",
    lblTopic: "Topic",
    lblMsg: "Message",
    topics: ["Holiday data correction", "Feature request", "Bug report", "Partnership / ads", "Other"],
    send: "Send message",
    sending: "Opening mail app...",
    willSendTo: "Will be sent to",
    sentVia: "sent via",
    sentTitle: "Your email is ready",
    sentDesc: "Your email app opened with a message to the hari.co.id team. Just hit send.",
    sendAnother: "Send another message",
    errName: "Name must be at least 2 characters",
    errEmail: "Invalid email",
    errMsg: "Message must be at least 10 characters",
  },
};